import React, { useState, useEffect, useContext } from 'react';
import { Box } from '@latitude/box';
import { Metadata } from '@latitude/metadata';
import {
  ALIGN,
  BREAKPOINT,
  COLOR
} from '@latitude/core/utils/constants';
import Layout from '@/components/layout';
import { REBRAND_COLOR, SITE_URL, TITLE_SUFFIX } from '@/utils/constants';
import DigitalWalletsLinks from './_links';
import faqs from './_faqs';
import { PageContext } from '@/context/PageContext';
import FeatureGridV1 from '@/components/FeatureGridV1/FeatureGridV1';
import Typography from '@latitude/typography';
import { getFeatureGridV1DataFromContentful } from '@/components/FeatureGridV1/contentfulFeatureGridV1';
import SvgInline from '@latitude/svg-inline';
import HeroV2 from '@/components/Hero/HeroV2/HeroV2';
import { AccordionFaqSidebar } from '@latitude/accordion-rebrand';
import Section from '@latitude/section-rebrand';
import ApplePaySetupAccordion from '@/components/PaymentSetupAccordion/AppleSetupAccordion';
import phoneImageUrl from './images/google-pay.gif';
import { ImportantInformation } from '@latitude/important-information-rebrand';
import styled from 'styled-components';

const FEATURE_GRID_DATA = {
  features: [
    {
      icon: <SvgInline name="icon-percent-rebrand" />,
      headingText: 'Let your budget breathe',
      paragraphText:
        'Enjoy a 13.99% p.a. purchase rate, so you can stretch your budget without stressing it.'
    },
    {
      icon: <SvgInline name="icon-confetti-rebrand" />,
      headingText: 'Earn on everyday spend',
      paragraphText: (
        <>
          Get 3% back in Latitude Rewards<sup>1</sup> when you set up recurring payments direct with participating utilities, telco,
          and streaming services.<a href="https://assets.latitudefinancial.com/legals/terms-conditions/lowrate-rewards.pdf" target="_blank" rel="noopener noreferrer" style={{
            textUnderlineOffset: '3px',
            color: '#535862',
            textDecoration: 'underline',
            borderBottom: 'none'
          }}> T&Cs apply</a>.
        </>
      ),

    },
    {
      icon: <SvgInline name="icon-users-rebrand" />,
      headingText: 'Team up for even more rewards',
      paragraphText: `Add an additional cardholder at no extra cost and become the ultimate rewards-earning team.`,
      learnMoreUrl:
        'https://latitudefs.zendesk.com/hc/en-au/articles/36591875170577-Additional-Cardholders'
    },
    {
      icon: <SvgInline name="icon-seal-check-rebrand" />,
      headingText: 'Travel with peace of mind',
      paragraphText:
        'Wherever you go, our specialist team keeps your personal info safe from fraud and identity theft.',
      learnMoreUrl: 'https://www.latitudefinancial.com.au/scams-hub/'
    }
  ]
};

const GOOGLE_PAY_SETUP_ITEMS = [
  {
    id: 'set-up-on-phone',
    title: 'Set up on phone',
    content: (
      <ul>
        <li>Download the Google Pay app, free from the Google Play Store.</li>
        <li>
          Open the Google Pay app and tap the &lsquo;+&rsquo; sign to add a
          card.
        </li>
        <li>
          Use your phone camera to capture your card&apos;s information or
          enter it manually.
        </li>
        <li>
          Follow the prompts to complete the details and tap &apos;Save&apos;
          to confirm that you agree to the Google Terms and Conditions.
        </li>
        <li>
          Read and confirm that you agree to the Latitude with Google Pay
          Terms and Conditions.
        </li>
        <li>
          For security purposes, we may ask you to call us or send you a
          verification code via SMS.
        </li>
      </ul>
    ),
  },
  {
    id: 'how-to-use',
    title: 'How to use',
    content: (
      <ul>
        <li>Wake your Google device.</li>
        <li>
          Hold your phone close to the payment terminal to pay.
        </li>
        <li>
          If your transaction is over $100 you may be prompted to input your
          card PIN.
        </li>
      </ul>
    ),
  },
];


const ExploreHeading = styled(Typography.H3)`
  text-align: center;
  @media (max-width: ${BREAKPOINT.SM}) {
    font-size: 32px;
  }`


const GooglePay = ({ location }) => {
  /** Contentful Data */
  const contentfulPageData = useContext(PageContext);
  const state = contentfulPageData?.updatedBody || {};

  const featureGridData =
    getFeatureGridV1DataFromContentful(state?.featureSliderData?.[0]) ||
    FEATURE_GRID_DATA;
  /** - END - */

  return (
    <Layout location={location}>
      <main className="navigation-spacer">
        <Metadata
          title={`Google Pay & Wallet Setup | Mobile Tap & Pay |  ${TITLE_SUFFIX}`}
          description="Google Pay is now available to Latitude customers. See how to setup Google Pay on your Android devices and make your payments more simple and secure."
          canonical={`${SITE_URL}${location.pathname}`}
        />
        {state?.heroBannerData?.[0] ? (
          <HeroV2 {...state?.heroBannerData[0]} />
        ) : ''
        }
        <Section css={{ paddingTop: '32px', paddingBottom: '32px' }}>
          <ExploreHeading

          >
            Explore the range of ways to pay
          </ExploreHeading>
        </Section>
        <DigitalWalletsLinks location={location} />
        <FeatureGridV1
          title={<Typography.H4 color={REBRAND_COLOR.INDIGO}>
            Why Google Pay?
          </Typography.H4>}
          id="benefits"
          trackingId="lowrate"
          {...featureGridData}
        />
        <ApplePaySetupAccordion items={GOOGLE_PAY_SETUP_ITEMS} heading="How to set up Google Pay" phoneImageUrl={phoneImageUrl} />
        <Box.Section
          backgroundColor={COLOR.GREY6}
          css={`
            padding: 8px 0;
            @media (min-width: ${BREAKPOINT.LG}) {
              padding: 40px 0;
            }
            .accordion__item-body .accordion__item-body {
              padding-top: 16px !important;
            }
          `}
        >
          <Section>
            <Typography.H4
              color={REBRAND_COLOR.INDIGO}
              className="mb-5"
            >
              Frequently asked questions
            </Typography.H4>
            <AccordionFaqSidebar data={faqs.googlePay} />
          </Section>
        </Box.Section>
        <ImportantInformation
          data={{
            content: {
              importantInformationSectionOne: [
                '<sup>&gt;</sup> Google Pay is available on compatible Android phones with Near Field Communications (NFC) technology. <a href="https://support.google.com/pay/answer/7625055" target="_blank">https://support.google.com/pay/answer/7625055</a>'
              ],
              importantInformationSectionTwo: [
                'Android, Google Pay and Google Play are trademarks of Google Inc.'
              ]
            }
          }}
          sectionOneColummClasses="col-10 offset-1 col-lg-5 offset-lg-1"
          sectionTwoColummClasses="col-10 offset-1 col-lg-5 offset-lg-0"
          {...state?.importantInfoData?.[0]}
        />
      </main>
    </Layout>
  );
}

export default GooglePay;
